import {
  Action,
  TableColumn,
} from "@/modules/shared/ui/components/custom/CustomTable"
import { ExpandableDataTable } from "@/modules/shared/ui/components/custom/ExpandableDataTable"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { useEffect, useState, useMemo } from "react"
import { ApplicationListItem } from "../../dtos/ApplicantListItem"
import { ApplicationDetails } from "../../dtos/ApplicationDetails"
import { useNavigate, useParams, useLocation, Link } from "react-router-dom"
import { DeleteConfirmationDialog } from "@/modules/recruitment/vacancies/ui/components/DeleteConfirmationDialog"
import { Badge } from "@/modules/shared/ui/components/badge"
import { useApplicants } from "../hooks/useApplicants"
import { SearchFilterBar } from "@/modules/shared/ui/components/custom/SearchFilterBar"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { toast } from "sonner"
import { useApplicationSignalR } from "../../hooks/useApplicationSignalR"
import { UploadCvDialog } from "../components/UploadCvDialog"
import { AcceptCandidateDialog } from "../components/AcceptCandidateDialog"
import { RejectCandidateDialog } from "../components/RejectCandidateDialog"
import {
  GetApplicationsForVacancy,
  rejectCandidate,
  hireCandidate,
} from "../../applicants-service"
import { useCvUpload } from "@/modules/recruitment/hooks/useCvUpload"
import { vacancyService } from "@/modules/recruitment/infrastructure/vacancy-service"
import { Button } from "@/modules/shared/ui/components/button"
import {
  ArrowLeft,
  Upload,
  Check,
  X,
  Bot,
  Users,
  Fingerprint,
  Video,
  Info,
} from "lucide-react"
import { EmployeeFormValues } from "@/modules/onboarding/ui/components/EmployeeForm"
import {
  getApplicationStageLabel,
  getApplicationStageBadgeVariant,
  ApplicationStage,
} from "../../enums/CandidateStatus"
import { parseApplicationStage } from "../../dtos/ApplicationDetails"
import { InterviewStatus } from "@/modules/recruitment/applications/enums/InterviewStatus"
import { InterviewType } from "@/modules/recruitment/applications/enums/InterviewType"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/modules/shared/ui/components/tooltip"
import { CreateExpertInterviewDialog } from "../components/CreateExpertInterviewDialog"
import { InterviewQuestion } from "@/modules/public/dto/videoInterviewDto"
import { CreateVideoInterviewDialog } from "../components/CreateVideoInterviewDialog"
import { SendRegretDialog } from "@/modules/recruitment/vacancies/ui/components/SendRegretDialog"
import axios from "axios"
import { DebugAiCallDialog } from "../components/DebugAiCallDialog"
import { useUserProfile } from "@/modules/profile/hooks/useProfile"
import { IdVerificationDialog } from "../components/IdVerificationDialog"

export const ApplicantsPage = () => {
  type StageFilter = ApplicationStage | "All"
  const stageFilterOptions: { label: string; value: string }[] = [
    { label: "All", value: "All" },
    { label: "Applied", value: String(ApplicationStage.Applied) },
    { label: "Screened", value: String(ApplicationStage.Screened) },
    { label: "Candidate", value: String(ApplicationStage.Candidate) },
    { label: "Hired", value: String(ApplicationStage.Hired) },
    { label: "Rejected", value: String(ApplicationStage.Rejected) },
  ]

  const [searchTerm, setSearchTerm] = useState("")
  const [stageFilter, setStageFilter] = useState<StageFilter>("All")
  const navigate = useNavigate()
  const location = useLocation()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedApplicant, setSelectedApplicant] = useState<
    ApplicationListItem | undefined
  >(undefined)
  const [isDeleting] = useState(false)
  const { vacancyId } = useParams<{ vacancyId: string }>()
  const { createInterview, scheduleExpertInterview, createVideoInterview } =
    useApplicants()
  const { currentOrganization } = useOrganizationsService()
  const { data: userProfile } = useUserProfile()
  const [_, setApplicants] = useState<ApplicationListItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const vacancyTitle = location.state?.vacancyTitle as string | undefined
  const vacancyBaseScore = location.state?.baseScore ?? (0 as number)
  const [isUploadCvDialogOpen, setIsUploadCvDialogOpen] = useState(false)
  const { upload: uploadCvFile } = useCvUpload({
    organizationId: currentOrganization?.id ?? "",
    vacancyId: vacancyId ?? "",
    onCompleted: async () => {
      if (vacancyId && currentOrganization?.id) {
        const data = await GetApplicationsForVacancy(
          currentOrganization.id,
          vacancyId,
        )
        setApplicationDetails(data)
      }
    },
  })

  const [applicationDetails, setApplicationDetails] = useState<
    ApplicationDetails[]
  >([])
  const [isAcceptDialogOpen, setIsAcceptDialogOpen] = useState(false)
  const [selectedApplication, setSelectedApplication] =
    useState<ApplicationListItem | null>(null)
  const [isAccepting, setIsAccepting] = useState(false)
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [isExpertInterviewDialogOpen, setIsExpertInterviewDialogOpen] =
    useState(false)
  const [isSchedulingExpert, setIsSchedulingExpert] = useState(false)
  const [expertInterviewApplication, setExpertInterviewApplication] =
    useState<ApplicationDetails | null>(null)
  const [isVideoInterviewDialogOpen, setIsVideoInterviewDialogOpen] =
    useState(false)
  const [videoInterviewApplication, setVideoInterviewApplication] =
    useState<ApplicationDetails | null>(null)
  const [isCreatingVideoInterview, setIsCreatingVideoInterview] =
    useState(false)
  const [showSendRegretDialog, setShowSendRegretDialog] = useState(false)
  const [hiredCandidateName, setHiredCandidateName] = useState<string>("")
  const [isPhoneRequiredDialogOpen, setIsPhoneRequiredDialogOpen] =
    useState(false)
  const [isIdVerificationDialogOpen, setIsIdVerificationDialogOpen] =
    useState(false)
  const [idVerificationApplication, setIdVerificationApplication] =
    useState<ApplicationDetails | null>(null)

  const getDetailedActions = (
    application: ApplicationDetails,
  ): Action<ApplicationDetails>[] => {
    const baseActions: Action<ApplicationDetails>[] = [
      {
        label: "View Applicant",
        onClick: (app) => {
          navigate(`/recruitment/applications/${app.id}`)
        },
      },
    ]
    const stage = parseApplicationStage(application.currentStage)

    if (
      stage !== ApplicationStage.Hired &&
      stage !== ApplicationStage.Rejected
    ) {
      baseActions.push(
        {
          label: "Create screening call",
          onClick: (app) => handleAiScreenCall(app),
        },
        {
          label: "Schedule Expert Interview",
          onClick: (app) => {
            setExpertInterviewApplication(app)
            setIsExpertInterviewDialogOpen(true)
          },
        },
      )

      const hasVideoInterview = application.interviews?.some(
        (i) => i.type === InterviewType[InterviewType.VideoScreening],
      )
      if (!hasVideoInterview) {
        baseActions.push({
          label: "Create Video Interview",
          onClick: (app) => {
            setVideoInterviewApplication(app)
            setIsVideoInterviewDialogOpen(true)
          },
        })
      }

      baseActions.push({
        label: "Run ID Verification",
        onClick: (app) => {
          setIdVerificationApplication(app)
          setIsIdVerificationDialogOpen(true)
        },
      })
    }

    if (
      stage !== ApplicationStage.Hired &&
      stage !== ApplicationStage.Rejected
    ) {
      baseActions.push(
        {
          label: "Accept Candidate",
          onClick: (app) => {
            setSelectedApplication(app)
            setIsAcceptDialogOpen(true)
          },
        },
        {
          label: "Reject Candidate",
          onClick: (app) => {
            setSelectedApplication(app)
            setIsRejectDialogOpen(true)
          },
        },
      )
    }
    return baseActions
  }

  const handleSendRegretMessages = async () => {
    if (!vacancyId || !currentOrganization?.id) return
    try {
      const result = await vacancyService.notifyClosedVacancyCandidates(
        currentOrganization.id,
        vacancyId,
      )
      if (result.isSuccess && result.value !== undefined) {
        toast.success(`Regret messages sent to ${result.value} candidates`)
      } else {
        toast.error(result.error ?? "Failed to send regret messages")
      }
      setShowSendRegretDialog(false)
    } catch (error: unknown) {
      toast.error((error as Error)?.message ?? "Failed to send regret messages")
    }
  }

  const handleNewApplication = (newApplication: ApplicationListItem) => {
    setApplicants((prev) => {
      if (prev.some((app) => app.id === newApplication.id)) return prev
      return [newApplication, ...prev]
    })
    toast.success(
      `New application from ${newApplication.candidate.firstName} ${newApplication.candidate.lastName}` +
      (newApplication.matchScore
        ? ` (${newApplication.matchScore?.score ?? 0}% match)`
        : ""),
      { duration: 5000 },
    )
  }

  useApplicationSignalR(
    currentOrganization?.id,
    vacancyId,
    handleNewApplication,
    (updatedApplication) => {
      setApplicationDetails((prev) =>
        prev.map((app) =>
          app.id === updatedApplication.id
            ? ({
              ...app,
              ...updatedApplication,
              // SignalR notifications use ApplicationDto which doesn't include
              // isIdVerificationCompleted — preserve the value already in state
              // so a call webhook update doesn't silently clear the green dot.
              isIdVerificationCompleted: app.isIdVerificationCompleted,
            } as ApplicationDetails)
            : app,
        ),
      )
      setApplicants((prev) =>
        prev.map((app) =>
          app.id === updatedApplication.id
            ? { ...app, ...updatedApplication }
            : app,
        ),
      )
    },
  )

  useEffect(() => {
    const fetchApplicants = async () => {
      if (!vacancyId || !currentOrganization?.id) return
      setIsLoading(true)
      try {
        const data = await GetApplicationsForVacancy(
          currentOrganization.id,
          vacancyId,
        )
        setApplicationDetails(data)
      } catch (error) {
        console.error("Error fetching applicants:", error)
        toast.error("Failed to load applicants")
      } finally {
        setIsLoading(false)
      }
    }
    fetchApplicants()
  }, [vacancyId, currentOrganization?.id])

  const handleDeleteApplicant = () => {
    if (!selectedApplicant) return
  }

  const handleUploadCv = (files: File[]) => {
    setIsUploadCvDialogOpen(false)
    uploadCvFile(files)
  }

  const handleVideoInterviewCreate = async (data: {
    questions: Partial<InterviewQuestion>[]
  }) => {
    if (!videoInterviewApplication) return
    setIsCreatingVideoInterview(true)
    try {
      await createVideoInterview({
        applicationId: videoInterviewApplication.id,
        questions: data.questions,
        maxVideoAttempts: 3, // Default for now
        videoDeadline: undefined, // Default for now
      })
      toast.success("Video interview invite sent successfully")
      setIsVideoInterviewDialogOpen(false)
      setVideoInterviewApplication(null)
      if (vacancyId && currentOrganization?.id) {
        const updated = await GetApplicationsForVacancy(
          currentOrganization.id,
          vacancyId,
        )
        setApplicationDetails(updated)
      }
    } catch (error) {
      console.error(error)
      toast.error("Failed to create video interview")
    } finally {
      setIsCreatingVideoInterview(false)
    }
  }

  const handleAcceptCandidate = async (
    contractFile: File,
    employeeData: EmployeeFormValues,
  ) => {
    if (!currentOrganization?.id || !selectedApplication) return
    setIsAccepting(true)
    try {
      await hireCandidate(
        selectedApplication.id,
        selectedApplication.candidate.id,
        contractFile,
        employeeData.jobStartDate,
        employeeData.workflowTemplateIds,
        employeeData.documentsNeeded,
      )
      const candidateName = `${selectedApplication.candidate.firstName} ${selectedApplication.candidate.lastName}`
      setHiredCandidateName(candidateName)
      toast.success(`${candidateName} accepted successfully`)
      setIsAcceptDialogOpen(false)
      setSelectedApplication(null)
      if (vacancyId) {
        const data = await GetApplicationsForVacancy(
          currentOrganization.id,
          vacancyId,
        )
        setApplicationDetails(data)
      }

      // Show regret dialog after hiring
      setShowSendRegretDialog(true)
    } catch (error: unknown) {
      console.error("Error accepting candidate:", error)
      if (axios.isAxiosError(error)) {
        const backendMessage =
          error.response?.data?.error ??
          error.response?.data?.message ?? // GlobalExceptionHandler shape
          "Failed to accept candidate"
        toast.error(backendMessage)
      } else {
        toast.error("Failed to accept candidate")
      }
    } finally {
      setIsAccepting(false)
    }
  }

  const handleExpertInterviewSchedule = async (data: {
    title: string
    notes: string
    timeSlots: { startTime: string; endTime: string }[]
  }) => {
    if (!expertInterviewApplication) return
    setIsSchedulingExpert(true)
    try {
      await scheduleExpertInterview({
        applicationId: expertInterviewApplication.id,
        title: data.title,
        notes: data.notes,
        timeSlots: data.timeSlots,
      })
      toast.success(
        "Expert interview scheduled successfully. Candidate has been notified.",
      )
      setIsExpertInterviewDialogOpen(false)
      setExpertInterviewApplication(null)
      if (vacancyId && currentOrganization?.id) {
        const updated = await GetApplicationsForVacancy(
          currentOrganization.id,
          vacancyId,
        )
        setApplicationDetails(updated)
      }
    } catch (error) {
      console.error("Failed to schedule expert interview", error)
      toast.error("Failed to schedule expert interview")
    } finally {
      setIsSchedulingExpert(false)
    }
  }

  const handleAiScreenCall = async (app: ApplicationDetails) => {
    if (!import.meta.env.PROD) {
      if (!userProfile?.phoneNumber) {
        setIsPhoneRequiredDialogOpen(true)
        return
      }
      await toast.promise(createInterview(app.id), {
        loading: "Creating screening call...",
        success: "Screening call created successfully",
        error: (err) =>
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to create screening call",
      })
    }
  }

  const handleRejectCandidate = async (reason: string) => {
    if (!currentOrganization?.id || !selectedApplication) return
    setIsRejecting(true)
    try {
      await rejectCandidate(
        currentOrganization.id,
        selectedApplication.id,
        reason,
      )
      toast.success(
        `${selectedApplication.candidate.firstName} ${selectedApplication.candidate.lastName} rejected`,
      )
      setIsRejectDialogOpen(false)
      setSelectedApplication(null)
      if (vacancyId) {
        const data = await GetApplicationsForVacancy(
          currentOrganization.id,
          vacancyId,
        )
        setApplicationDetails(data)
      }
    } catch (error) {
      console.error("Error rejecting candidate:", error)
      toast.error("Failed to reject candidate")
    } finally {
      setIsRejecting(false)
    }
  }

  const filteredApplicationDetails = useMemo(() => {
    const term = searchTerm.toLowerCase().trim()
    return applicationDetails.filter((app) => {
      const stage = parseApplicationStage(app.currentStage)
      const matchesStage = stageFilter === "All" || stage === stageFilter
      const matchesSearch =
        !term ||
        `${app.candidate.firstName} ${app.candidate.lastName}`.toLowerCase().includes(term) ||
        app.candidate.email.toLowerCase().includes(term)
      return matchesStage && matchesSearch
    })
  }, [applicationDetails, stageFilter, searchTerm])

  const columns: TableColumn<ApplicationDetails>[] = [
    {
      key: "candidate",
      label: "Name",
      render: (application: ApplicationDetails) => (
        <div className="flex flex-col min-w-0 max-w-md">
          <button
            className="font-medium text-foreground truncate hover:text-primary hover:underline text-left"
            onClick={() =>
              navigate(`/recruitment/applications/${application.id}`)
            }
          >
            {application.candidate.firstName} {application.candidate.lastName}
          </button>
          <span className="text-xs text-muted-foreground">
            {application.candidate.email}
          </span>
        </div>
      ),
    },
    {
      key: "candidateId",
      label: "Status",
      headerClassName: "text-center",
      render: (application: ApplicationDetails) => {
        const stage = parseApplicationStage(application.currentStage)
        return (
          <div className="flex flex-col min-w-0 max-w-md">
            <div className="text-center">
              <Badge variant={getApplicationStageBadgeVariant(stage)}>
                {getApplicationStageLabel(stage)}
              </Badge>
            </div>
          </div>
        )
      },
    },
    {
      key: "matchScore",
      label: "Match Score",
      headerClassName: "text-center",
      render: (application: ApplicationDetails) => {
        const isBelowBase = application.matchScore.score < vacancyBaseScore
        return (
          <div className="text-center">
            <div
              className={`inline-flex items-center gap-1.5 text-sm font-semibold ${isBelowBase ? "text-red-700" : "text-green-700"}`}
            >
              {application.matchScore.score}%
              {isBelowBase && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-red-400 cursor-help shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[260px] p-0 overflow-hidden"
                  >
                    <div className="px-3.5 py-2.5 space-y-1">
                      <div className="flex items-center gap-2">
                        <Info className="w-3.5 h-3.5 shrink-0 text-red-400" />
                        <span className="text-[13px] font-semibold text-white leading-tight">
                          Below Base Score
                        </span>
                      </div>
                      <p className="text-[11px] items-center leading-relaxed text-muted-foreground">
                        This applicant scored {application.matchScore.score}%,
                        which is below the vacancy rejection score of{" "}
                        {vacancyBaseScore}%.
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        )
      },
    },
    {
      key: "appliedAt",
      label: "Applied",
      headerClassName: "text-center",
      render: (application: ApplicationDetails) => (
        <div className="text-center">
          <span className="text-sm text-muted-foreground">
            {new Date(application.appliedAt).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>
      ),
    },
    {
      key: "interviews",
      label: "Screening",
      headerClassName: "text-center",
      render: (application: ApplicationDetails) => {
        const hasCompletedScreening = application.interviews?.some(
          (interview) =>
            interview.type === InterviewType[InterviewType.AiScreening] &&
            interview.status === InterviewStatus[InterviewStatus.Completed],
        )
        const hasCompletedExpert = application.interviews?.some(
          (i) =>
            i.type === InterviewType[InterviewType.ExpertSession] &&
            i.status === InterviewStatus[InterviewStatus.Completed],
        )

        const steps = [
          { label: "AI Screening", description: "Initial AI-powered screening assessment", icon: Bot, completed: hasCompletedScreening, accentColor: "violet" },
          { label: "Expert Session", description: "One-on-one interview with domain expert", icon: Users, completed: hasCompletedExpert, accentColor: "blue" },
          {
            label: "Video Interview",
            description: "Asynchronous video interview",
            icon: Video,
            completed: application.interviews?.some(
              (i) =>
                i.type === InterviewType[InterviewType.VideoScreening] &&
                i.status === InterviewStatus[InterviewStatus.Completed],
            ),
            accentColor: "purple",
          },
          { label: "ID Verification", description: "IDAS identity verification check", icon: Fingerprint, completed: application.isIdVerificationCompleted, accentColor: "teal" },
        ] as const

        return (
          <TooltipProvider>
            <div className="flex items-center justify-center gap-1">
              <div className="flex items-center gap-1.5">
                {steps.map((step, idx) => {
                  const Icon = step.icon
                  const isComplete = !!step.completed
                  return (
                    <Tooltip key={idx}>
                      <TooltipTrigger asChild>
                        <div
                          className={`group relative w-6 h-6 rounded-full flex items-center justify-center cursor-help transition-all duration-300 ease-out ${isComplete
                              ? "bg-linear-to-br from-green-400 to-emerald-500 shadow-sm shadow-green-200 hover:shadow-md hover:shadow-green-300 hover:scale-110"
                              : "bg-muted border border-border hover:bg-muted hover:border-border hover:scale-110"
                            }`}
                        >
                          {isComplete ? (
                            <Check
                              className="w-3.5 h-3.5 text-white drop-shadow-sm"
                              strokeWidth={2.5}
                            />
                          ) : (
                            <Icon className="w-3 h-3 text-muted-foreground group-hover:text-muted-foreground transition-colors duration-200" />
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-[200px] p-0 overflow-hidden"
                      >
                        <div className="px-3.5 py-2.5 space-y-1">
                          <div className="flex items-center gap-2">
                            <Icon
                              className={`w-3.5 h-3.5 shrink-0 ${isComplete ? "text-green-400" : "text-muted-foreground"
                                }`}
                            />
                            <span className="text-[13px] font-semibold text-white leading-tight">
                              {step.label}
                            </span>
                          </div>
                          <p className="text-[11px] leading-relaxed text-muted-foreground">
                            {step.description}
                          </p>
                          <div
                            className={`inline-flex items-center gap-1 mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-medium ${isComplete
                                ? "bg-green-500/20 text-green-300"
                                : "bg-white/10 text-muted-foreground"
                              }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${isComplete ? "bg-green-400" : "bg-muted-foreground"
                                }`}
                            />
                            {isComplete ? "Completed" : "Pending"}
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  )
                })}
              </div>
            </div>
          </TooltipProvider>
        )
      },
    },
  ]

  const renderSkillBreakdown = (row: ApplicationDetails) => {
    const { matchScore } = row
    const categoryOrder = [
      "RequiredSkills",
      "OptionalSkills",
      "KeyResponsibilities",
      "Certification",
    ]
    const categoryConfig: Record<
      string,
      { label: string; badgeClass: string; iconColor: string }
    > = {
      RequiredSkills: {
        label: "Required Skills",
        badgeClass: "bg-red-50 text-red-700 border-red-200",
        iconColor: "text-red-500",
      },
      OptionalSkills: {
        label: "Optional Skills",
        badgeClass: "bg-blue-50 text-blue-700 border-blue-200",
        iconColor: "text-blue-500",
      },
      KeyResponsibilities: {
        label: "Key Responsibilities",
        badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
        iconColor: "text-amber-500",
      },
      Certification: {
        label: "Certifications",
        badgeClass: "bg-purple-50 text-purple-700 border-purple-200",
        iconColor: "text-purple-500",
      },
    }

    const grouped = matchScore.skillBreakdown.reduce<
      Record<string, typeof matchScore.skillBreakdown>
    >((acc, skill) => {
      const cat = skill.category || "Other"
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(skill)
      return acc
    }, {})

    const sortedCategories = [
      ...categoryOrder.filter((c) => grouped[c]),
      ...Object.keys(grouped).filter((c) => !categoryOrder.includes(c)),
    ]

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-foreground">
            Skill Breakdown
          </h4>
        </div>

        {sortedCategories.map((category) => {
          const config = categoryConfig[category] || {
            label: category,
            badgeClass: "bg-muted text-foreground border-border",
            iconColor: "text-muted-foreground",
          }
          const skills = grouped[category]

          return (
            <div key={category} className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={`text-[11px] px-2 py-0.5 font-semibold border ${config.badgeClass}`}
                >
                  {config.label}
                </Badge>
                <span className="text-[10px] text-muted-foreground">
                  {skills.length} item{skills.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="grid gap-2">
                {skills.map((skill, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-foreground">
                          {skill.skillName}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {skill.explanation}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="w-32">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-muted-foreground">
                            Match
                          </span>
                          <span className="text-xs font-semibold text-foreground">
                            {skill.matchPercentage}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${skill.matchPercentage >= 80
                                ? "bg-green-500"
                                : skill.matchPercentage >= 60
                                  ? "bg-yellow-500"
                                  : "bg-red-400"
                              }`}
                            style={{ width: `${skill.matchPercentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}

        {(matchScore.strengths.length > 0 ||
          matchScore.weaknesses.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
              <div>
                <h5 className="text-xs font-semibold text-green-700 mb-1.5">
                  Strengths
                </h5>
                {matchScore.strengths.length > 0 ? (
                  <ul className="space-y-1 max-h-40 overflow-y-auto">
                    {matchScore.strengths.map((s, i) => (
                      <li
                        key={i}
                        className="text-xs text-muted-foreground flex items-start gap-1.5"
                      >
                        <Check className="w-3 h-3 text-green-500 mt-0.5 shrink-0" />
                        <span className="wrap-break-words">{s}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground italic">
                    No strengths identified
                  </p>
                )}
              </div>
              <div>
                <h5 className="text-xs font-semibold text-red-700 mb-1.5">
                  Weaknesses
                </h5>
                {matchScore.weaknesses.length > 0 ? (
                  <ul className="space-y-1 max-h-40 overflow-y-auto">
                    {matchScore.weaknesses.map((w, i) => (
                      <li
                        key={i}
                        className="text-xs text-muted-foreground flex items-start gap-1.5"
                      >
                        <X className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />
                        <span className="wrap-break-words">{w}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground italic">
                    No weaknesses identified
                  </p>
                )}
              </div>
            </div>
          )}
      </div>
    )
  }

  return (
    <div className="p-4 space-y-6">
      <div className="bg-card rounded-lg border border-border p-8 shadow-sm">
        <Link
          to="/recruitment/vacancies"
          className="text-sm font-semibold inline-flex items-center gap-2 border border-transparent hover:text-primary text-muted-foreground transition-all duration-200 h-10 px-3 rounded-md"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Vacancies
        </Link>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-foreground">
            {vacancyTitle ? `Applicants for ${vacancyTitle}` : "Applicants"}
          </h1>
          <div className="flex items-center gap-2">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={() => setIsUploadCvDialogOpen(true)}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Candidate CV
            </Button>
          </div>
        </div>

        <SearchFilterBar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search applicants..."
          filterValue={String(stageFilter)}
          onFilterChange={(value) =>
            setStageFilter(
              value === "All" ? "All" : (Number(value) as ApplicationStage),
            )
          }
          filterOptions={stageFilterOptions}
          filterPlaceholder="Status"
          count={filteredApplicationDetails.length}
          countLabel="Applicants"
          hasData={applicationDetails.length > 0}
          defaultFilterValue="All"
        />
      </div>

      {/* Applicants Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner className="size-6" />
        </div>
      ) : (
        <ExpandableDataTable
          columns={columns}
          data={[...filteredApplicationDetails].sort(
            (a, b) => b.matchScore.score - a.matchScore.score,
          )}
          onRowClick={(applicant) =>
            navigate(`/recruitment/applications/${applicant.id}`)
          }
          actions={getDetailedActions}
          renderExpandedRow={renderSkillBreakdown}
          emptyMessage={
            searchTerm
              ? "No applicants found matching your search."
              : "No applicants available."
          }
        />
      )}
      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsDeleteDialogOpen(open)
          if (!open) setSelectedApplicant(undefined)
        }}
        onConfirm={handleDeleteApplicant}
        title="Delete Applicant"
        description={`Are you sure you want to delete "${selectedApplicant?.candidate.firstName} ${selectedApplicant?.candidate.lastName}"? This action cannot be undone.`}
        isDeleting={isDeleting}
      />
      <SendRegretDialog
        open={showSendRegretDialog}
        onOpenChange={setShowSendRegretDialog}
        onConfirm={handleSendRegretMessages}
        vacancyTitle={vacancyTitle || "this position"}
        hiredCandidateName={hiredCandidateName}
      />
      <UploadCvDialog
        open={isUploadCvDialogOpen}
        onOpenChange={setIsUploadCvDialogOpen}
        onConfirm={handleUploadCv}
        applicantName="new applicant"
      />
      <AcceptCandidateDialog
        open={isAcceptDialogOpen}
        onOpenChange={(open) => {
          setIsAcceptDialogOpen(open)
          if (!open) setSelectedApplication(null)
        }}
        onConfirm={handleAcceptCandidate}
        candidateName={
          selectedApplication
            ? `${selectedApplication.candidate.firstName} ${selectedApplication.candidate.lastName}`
            : ""
        }
        candidateData={
          selectedApplication
            ? {
              firstName: selectedApplication.candidate.firstName,
              lastName: selectedApplication.candidate.lastName,
              email: selectedApplication.candidate.email,
              jobTitle:
                vacancyTitle || selectedApplication.vacancyTitle || "",
              documentsNeeded: [],
            }
            : undefined
        }
        formId="accept-candidate-form"
        availableJobs={[]}
        isUploading={isAccepting}
      />
      <RejectCandidateDialog
        open={isRejectDialogOpen}
        onOpenChange={(open) => {
          setIsRejectDialogOpen(open)
          if (!open) setSelectedApplication(null)
        }}
        onConfirm={handleRejectCandidate}
        candidateName={
          selectedApplication
            ? `${selectedApplication.candidate.firstName} ${selectedApplication.candidate.lastName}`
            : ""
        }
        isRejecting={isRejecting}
      />
      <CreateExpertInterviewDialog
        open={isExpertInterviewDialogOpen}
        onOpenChange={(open) => {
          setIsExpertInterviewDialogOpen(open)
          if (!open) setExpertInterviewApplication(null)
        }}
        onConfirm={handleExpertInterviewSchedule}
        isSubmitting={isSchedulingExpert}
      />
      <CreateVideoInterviewDialog
        open={isVideoInterviewDialogOpen}
        onOpenChange={(open) => {
          setIsVideoInterviewDialogOpen(open)
          if (!open) setVideoInterviewApplication(null)
        }}
        onConfirm={handleVideoInterviewCreate}
        isSubmitting={isCreatingVideoInterview}
        vacancyId={vacancyId}
        organizationId={currentOrganization?.id}
      />
      <DebugAiCallDialog
        open={isPhoneRequiredDialogOpen}
        onOpenChange={setIsPhoneRequiredDialogOpen}
      />
      <IdVerificationDialog
        open={isIdVerificationDialogOpen}
        onOpenChange={(open) => {
          setIsIdVerificationDialogOpen(open)
          if (!open) setIdVerificationApplication(null)
        }}
        candidateId={idVerificationApplication?.candidateId}
        candidateName={
          idVerificationApplication
            ? `${idVerificationApplication.candidate.firstName} ${idVerificationApplication.candidate.lastName}`
            : ""
        }
        organizationId={currentOrganization?.id}
        prefilledIdNumber={idVerificationApplication?.candidate.idNumber}
        onVerificationCompleted={(verifiedCandidateId) => {
          setApplicationDetails((prev) =>
            prev.map((app) =>
              app.candidateId === verifiedCandidateId
                ? { ...app, isIdVerificationCompleted: true }
                : app,
            ),
          )
        }}
      />
    </div>
  )
}
